import { Box, CircularProgress, Fade } from '@mui/material'
import { Suspense, useState } from 'react'
import { Route, Routes } from 'react-router-dom'

import AdminLayout from './admin/AdminLayout'
import { useAuth } from './auth/useAuth'
import { useBootstrap } from './auth/useBootstrap'
import BootstrapErrorPage from './components/BootstrapErrorPage'
import QueryErrorBoundary from './components/QueryErrorBoundary'
import LiveDashboardPage from './dashboards/LiveDashboardPage'
import HomePage from './home/HomePage'
import OrganizationHub from './home/OrganizationHub'
import MainLayout from './layout/MainLayout'
import TopBarOnlyLayout from './layout/TopBarOnlyLayout'
import WeDryLayout from './products/WeDry/Layout'
import WePlaneLayout from './products/WePlane/Layout'
import WeSawLayout from './products/WeSaw/Layout'
import WeScheduleLayout from './products/WeSchedule/Layout'
import WeTrackLayout from './products/WeTrack/Layout'
import QualityTestCreator from './qualityControl/QualityTestCreator'
import QualityTestEditor from './qualityControl/QualityTestEditor'
import QualityTestList from './qualityControl/QualityTestList'
import TestExecutionPage from './qualityControl/TestExecutionPage'
import TestExecutionViewer from './qualityControl/TestExecutionViewer'
import TestHistoryPage from './qualityControl/TestHistoryPage'
import ReportPlaceholder from './reports/viewer/ReportPlaceholder'
import ReportViewerWrapper from './reports/viewer/ReportViewerWrapper'
import { useAppSelector } from './store/store.hooks'
import UserSettings from './user/UserSettings'

const App = () => {
  useAuth()
  useBootstrap()

  const isReady = useAppSelector((state) => state.auth.isReady)
  const bootstrapError = useAppSelector((state) => state.auth.bootstrapError)
  const [isConnected] = useState(true)

  if (isReady && bootstrapError) {
    return <BootstrapErrorPage />
  }

  if (!isReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Fade in style={{ transitionDelay: '800ms' }} unmountOnExit>
          <CircularProgress color="secondary" size={100} />
        </Fade>
      </Box>
    )
  }
    
  return (
    <Routes>
      <Route element={<TopBarOnlyLayout />}>
        <Route path="/UserSettings" element={<UserSettings />} />
        <Route
          path="/admin"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress color="secondary" />
                  </Box>
                }
              >
                <AdminLayout />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
        <Route path="/hub" element={<OrganizationHub />} />
      </Route>
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/reports/:workspaceId/:reportId" element={<ReportViewerWrapper />} />
        <Route path="/adminReports/:workspaceId/:reportId" element={<ReportViewerWrapper />} />
        <Route path="/reports" element={<ReportPlaceholder />} />
        <Route path="/dashboards/:dashboardId" element={<LiveDashboardPage />} />
        <Route path="/WeSaw" element={<WeSawLayout isConnected={isConnected} />} />
        <Route path="/WePlane" element={<WePlaneLayout isConnected={isConnected} />} />
        <Route path="/WeTrack" element={<WeTrackLayout />} />
        <Route path="/WeSchedule" element={<WeScheduleLayout />} />
        <Route path="/WeDry" element={<WeDryLayout />} />
        <Route
          path="/quality-control"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress color="secondary" size={75} />
                  </Box>
                }
              >
                <QualityTestList />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
        <Route
          path="/quality-control/:testId/execute"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress color="secondary" size={75} />
                  </Box>
                }
              >
                <TestExecutionPage />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
        <Route
          path="/quality-control/:testId/history"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress color="secondary" size={75} />
                  </Box>
                }
              >
                <TestHistoryPage />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
        <Route
          path="/quality-control/:testId/history/:executionId"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress color="secondary" size={75} />
                  </Box>
                }
              >
                <TestExecutionViewer />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
        <Route
          path="/quality-control/create"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress color="secondary" size={75} />
                  </Box>
                }
              >
                <QualityTestCreator />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
        <Route
          path="/quality-control/:testDefinitionId/edit"
          element={
            <QueryErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress color="secondary" size={75} />
                  </Box>
                }
              >
                <QualityTestEditor />
              </Suspense>
            </QueryErrorBoundary>
          }
        />
      </Route>
    </Routes>
  )
}

export default App
